import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "../api/axiosInstance";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "var(--erp-overlay, rgba(7, 15, 36, 0.78))",
  zIndex: 22000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "14px 12px",
};

const shellStyle = {
  width: "100%",
  maxWidth: "520px",
  borderRadius: "16px",
  padding: "28px",
  background: "var(--erp-modal-bg, #0e1730)",
  border: "1px solid var(--erp-modal-border, rgba(99, 193, 199, 0.2))",
  borderTop: "3px solid #3DB6E9",
  boxShadow: "var(--erp-modal-shadow, 0 18px 44px rgba(2, 8, 28, 0.28))",
  display: "flex",
  flexDirection: "column",
  maxHeight: "calc(100dvh - 28px)",
  overflowY: "auto",
};

const contentWrapperStyle = {};

const sectionStyle = {
  borderRadius: "16px",
  padding: "18px",
  background: "var(--erp-modal-section, #121f3d)",
  border: "1px solid var(--erp-modal-border, rgba(99, 193, 199, 0.2))",
  marginBottom: "18px",
};

const sectionHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "12px",
  flexWrap: "wrap",
};

const sectionTitleStyle = {
  margin: 0,
  fontSize: "1.05rem",
  fontWeight: 800,
  color: "var(--erp-modal-title, #f2f6ff)",
};

const sectionTextStyle = {
  fontSize: "0.78rem",
  color: "var(--erp-modal-muted, #aebbd0)",
  margin: "6px 0 0",
};

const fieldGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "12px",
  marginTop: "12px",
};

const labelStyle = {
  fontSize: "0.85rem",
  fontWeight: 600,
  color: "var(--erp-modal-text, #dfe8f7)",
  marginBottom: "4px",
  display: "block",
};

const inputFieldStyle = {
  width: "100%",
  borderRadius: "10px",
  padding: "10px 12px",
  background: "var(--erp-modal-input, #152344)",
  border: "1px solid var(--erp-modal-border-strong, rgba(61, 182, 233, 0.62))",
  color: "var(--erp-modal-text, #dfe8f7)",
  fontSize: "0.95rem",
};

const actionsRowStyle = {
  display: "flex",
  gap: "12px",
  marginTop: "16px",
};

const buttonBase = {
  borderRadius: "12px",
  padding: "12px 26px",
  fontWeight: 700,
  letterSpacing: "0.03em",
  cursor: "pointer",
  flex: 1,
  border: "none",
  color: "#fff",
};

const primaryButtonStyle = {
  ...buttonBase,
  background: "var(--erp-modal-primary, #223A7A)",
};

const secondaryButtonStyle = {
  ...buttonBase,
  border: "1px solid var(--erp-modal-border-strong, rgba(61, 182, 233, 0.62))",
  background: "var(--erp-modal-bg, #0e1730)",
  color: "var(--erp-modal-text, #dfe8f7)",
};

const savingOverlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "var(--erp-overlay, rgba(7, 15, 36, 0.78))",
  color: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "1.5rem",
  fontWeight: "bold",
  zIndex: 23000,
  pointerEvents: "none",
};

const toastOverlayStyle = {
  position: "fixed",
  top: "14px",
  left: 0,
  right: 0,
  display: "flex",
  justifyContent: "center",
  pointerEvents: "none",
  zIndex: 25000,
};

const toastCardStyle = {
  background: "var(--erp-modal-primary, #223A7A)",
  border: "1px solid #3DB6E9",
  borderRadius: "18px",
  padding: "14px 24px",
  color: "#ffffff",
  fontWeight: 700,
  boxShadow: "var(--erp-modal-shadow, 0 18px 44px rgba(2, 8, 28, 0.28))",
  maxWidth: "420px",
  width: "min(90vw, 420px)",
  textAlign: "center",
};

function toLocalISODate(value) {
  const d = new Date(value);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getStopSellDay(stopSell) {
  return String(stopSell?.stay_date || stopSell?.start_date || "").slice(0, 10);
}

export default function RemoveStopSellPopup({ show, onClose, formData, stopSells = [] }) {
  const roomRateId = formData?.room_rate?.id || formData?.room_rate_id || null;
  const [startDate, setStartDate] = useState(formData?.start_date || "");
  const [endDate, setEndDate] = useState(formData?.end_date || "");
  const [submitting, setSubmitting] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const toastTimer = useRef(null);

  const todayISO = useMemo(() => toLocalISODate(new Date()), []);

  useEffect(() => {
    return () => {
      if (toastTimer.current) {
        clearTimeout(toastTimer.current);
      }
    };
  }, []);

  if (!show) return null;

  const showToast = (message, closeAfter = true) => {
    if (toastTimer.current) {
      clearTimeout(toastTimer.current);
    }
    setToastMessage(message);
    if (closeAfter) {
      toastTimer.current = setTimeout(() => setToastMessage(""), 2000);
    }
  };

  const handleRangeDelete = async (mode) => {
    const requestedStartStr = String(startDate || "").slice(0, 10);
    const requestedEndStr = String(endDate || "").slice(0, 10);

    if (!requestedStartStr || !requestedEndStr) {
      alert("Invalid dates.");
      return;
    }
    if (requestedEndStr < requestedStartStr) {
      alert("End date must be after start date.");
      return;
    }
    if (requestedEndStr < todayISO) {
      alert("Selected range is in the past.");
      return;
    }

    const filtered = stopSells.filter((ss) => {
      if (String(ss.room_rate_id) !== String(roomRateId)) return false;
      const ssDay = getStopSellDay(ss);
      return ssDay && ssDay >= todayISO && ssDay >= requestedStartStr && ssDay <= requestedEndStr;
    });

    if (!filtered.length) {
      alert("No current/future stop sells found in this range.");
      return;
    }

    const confirmed = window.confirm(
      mode === "range"
        ? "This will remove the highlighted stop sells within the range."
        : "This will remove ALL future stop sells for this room rate."
    );
    if (!confirmed) return;

    const targetKeys = new Set(
      filtered.map((ss) => `${String(ss.room_rate_id)}|${getStopSellDay(ss)}`)
    );

    const finalizeSuccess = (message) => {
      showToast(message || "Stop sell(s) removed successfully.");
      onClose && onClose();
      window.location.reload();
    };

    const verifyAppliedAfterTimeout = async () => {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/stop-sells`,
        { withCredentials: true }
      );

      const freshStopSells = Array.isArray(data) ? data : [];
      const pendingTargets = freshStopSells.filter((ss) =>
        targetKeys.has(`${String(ss.room_rate_id)}|${getStopSellDay(ss)}`)
      );

      return pendingTargets.length === 0;
    };

    try {
      setSubmitting(true);
      if (mode === "range") {
        const { data } = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/stop-sells/delete-range`,
          {
            room_rate_id: roomRateId,
            start_date: requestedStartStr,
            end_date: requestedEndStr,
          },
          { withCredentials: true }
        );
        finalizeSuccess(data?.message || "Stop sell(s) removed successfully.");
      } else {
        for (const ss of filtered) {
          await axios.delete(`${process.env.REACT_APP_API_URL}/api/stop-sells/${ss.id}`, {
            withCredentials: true,
          });
        }
        finalizeSuccess("All stop sells removed.");
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 504) {
        try {
          const alreadyApplied = await verifyAppliedAfterTimeout();
          if (alreadyApplied) {
            finalizeSuccess("Stop sell(s) updated successfully.");
            return;
          }
        } catch (verificationError) {
          console.error("Error verifying stop sell removal after timeout:", verificationError);
        }
      }
      console.error("Error removing stop sells:", err);
      alert("Failed to update stop sells.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={shellStyle}>
        <div style={{ textAlign: "center", marginBottom: "18px" }}>
          <p
            style={{
              letterSpacing: "0.35em",
              fontSize: "0.8rem",
              color: "var(--erp-modal-muted, #aebbd0)",
              marginBottom: "6px",
              textTransform: "uppercase",
            }}
          >
            Room rate studio
          </p>
          <h2 style={{ margin: 0, fontSize: "1.35rem", color: "var(--erp-modal-title, #f2f6ff)" }}>Remove Stop Sell</h2>
        </div>
        <div style={contentWrapperStyle}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!roomRateId) {
                alert("Missing room rate.");
                return;
              }
              handleRangeDelete("range");
            }}
          >
            <section style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <div>
                  <h3 style={sectionTitleStyle}>Date range</h3>
                  <p style={sectionTextStyle}>Only today/future stop sells matching the rate will be deleted.</p>
                </div>
              </div>
              <div style={fieldGridStyle}>
                <div>
                  <label style={labelStyle}>Start date</label>
                  <input
                    type="date"
                    style={inputFieldStyle}
                    value={startDate}
                    min={todayISO}
                    onChange={(event) => setStartDate(event.target.value)}
                    required
                    disabled={submitting}
                  />
                </div>
                <div>
                  <label style={labelStyle}>End date</label>
                  <input
                    type="date"
                    style={inputFieldStyle}
                    value={endDate}
                    min={startDate || todayISO}
                    onChange={(event) => setEndDate(event.target.value)}
                    required
                    disabled={submitting}
                  />
                </div>
              </div>
            </section>
            <div style={actionsRowStyle}>
              <button type="submit" style={primaryButtonStyle} disabled={submitting}>
                {submitting ? "Removing..." : "Remove"}
              </button>
              <button
                type="button"
                style={{
                  ...secondaryButtonStyle,
                  borderColor: "var(--erp-modal-danger-border, #9B3445)",
                  background: "var(--erp-modal-danger-soft, rgba(155, 52, 69, 0.2))",
                  color: "var(--erp-danger, #ffc7cf)",
                }}
                onClick={() => {
                  if (!roomRateId) {
                    alert("Missing room rate.");
                    return;
                  }
                  handleRangeDelete("all");
                }}
                disabled={submitting}
              >
                Remove All
              </button>
              <button
                type="button"
                style={secondaryButtonStyle}
                onClick={() => onClose && onClose()}
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
      {submitting && <div style={savingOverlayStyle}>Updating stop sells…</div>}
      {toastMessage && (
        <div style={toastOverlayStyle}>
          <div style={toastCardStyle}>{toastMessage}</div>
        </div>
      )}
    </div>
  );
}
